import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  Col,
  Container,
  Row,
  Table,
} from "reactstrap";

import { provincesRepo } from "data/repositories/provincesRepo";
import { adminThemes } from "data/admin/domain";
import { adminPaths } from "admin/paths";
import { confirmAdminAction, sortByOption, themeColor } from "admin/contentUtils";
import AdminListControls from "components/admin/AdminListControls";
import AdminPageHeader from "components/admin/AdminPageHeader";
import AdminStateBlock from "components/admin/AdminStateBlock";

const sortOptions = [
  { value: "updated-desc", label: "Son güncellenen" },
  { value: "name-asc", label: "Ada göre A-Z" },
  { value: "name-desc", label: "Ada göre Z-A" },
  { value: "highlights-desc", label: "Highlight sayısı" },
];

export default function ProvincesList() {
  const location = useLocation();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [flash, setFlash] = useState(null);
  const [search, setSearch] = useState("");
  const [themeFilter, setThemeFilter] = useState("");
  const [stateFilter, setStateFilter] = useState("active");
  const [sortBy, setSortBy] = useState("updated-desc");

  const load = useCallback(() => {
    setLoading(true);
    setError("");

    try {
      setItems(provincesRepo.list({ includeDeleted: true }));
    } catch (loadError) {
      setError(loadError?.message || "İller yüklenemedi");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (!location.state?.status) return;
    setFlash(location.state.status);
    navigate(location.pathname, { replace: true, state: null });
  }, [location.pathname, location.state, navigate]);

  const visibleItems = useMemo(() => {
    const query = search.trim().toLowerCase();
    const filtered = items.filter((item) => {
      if (stateFilter === "active" && item.deletedAt) return false;
      if (stateFilter === "archived" && !item.deletedAt) return false;
      if (themeFilter && !(item.themes || []).includes(themeFilter)) return false;
      if (!query) return true;

      return [item.id, item.name, item.slogan, item.story, ...(item.themes || [])]
        .join(" ")
        .toLowerCase()
        .includes(query);
    });

    return sortByOption(filtered, sortBy, {
      "highlights-desc": (left, right) => (right.highlights || []).length - (left.highlights || []).length,
    });
  }, [items, search, sortBy, stateFilter, themeFilter]);

  const onArchive = (item) => {
    const ok = confirmAdminAction(item.name);
    if (!ok) return;

    try {
      provincesRepo.remove(item.id);
      setFlash({ color: "success", message: `${item.name} arşive alındı.` });
      load();
    } catch (archiveError) {
      setFlash({ color: "danger", message: archiveError?.message || "İl arşivlenemedi." });
    }
  };

  const onRestore = (item) => {
    try {
      provincesRepo.restore(item.id);
      setFlash({ color: "success", message: `${item.name} tekrar yayına alındı.` });
      load();
    } catch (restoreError) {
      setFlash({ color: "danger", message: restoreError?.message || "İl geri alınamadı." });
    }
  };

  const resetFilters = () => {
    setSearch("");
    setThemeFilter("");
    setStateFilter("active");
    setSortBy("updated-desc");
  };

  return (
    <>
      <AdminPageHeader
        gradient="info"
        title="İller Yönetimi"
        description="Province modeli mobile source of truth ile hizalı olarak yönetilir."
        primaryTo={{ tag: Link, to: `/admin${adminPaths.content.provinces.create}` }}
        primaryLabel="Yeni il"
      />

      <Container className="mt--6" fluid>
        <Row>
          <Col>
            <Card className="shadow">
              <CardHeader className="border-0">
                <h3 className="mb-3">Liste</h3>
                <AdminListControls
                  search={search}
                  onSearch={setSearch}
                  searchPlaceholder="ID, ad, slogan veya tema ara"
                  filters={[
                    {
                      key: "state",
                      label: "Kayıt durumu",
                      value: stateFilter,
                      onChange: setStateFilter,
                      options: [
                        { value: "active", label: "Aktif" },
                        { value: "archived", label: "Arşiv" },
                        { value: "all", label: "Tümü" },
                      ],
                    },
                    {
                      key: "theme",
                      label: "Tema",
                      value: themeFilter,
                      onChange: setThemeFilter,
                      options: [{ value: "", label: "Tüm temalar" }, ...adminThemes.map((theme) => ({ value: theme, label: theme }))],
                    },
                  ]}
                  sortValue={sortBy}
                  onSortChange={setSortBy}
                  sortOptions={sortOptions}
                  onReset={resetFilters}
                  resultCount={visibleItems.length}
                  totalCount={items.length}
                />
              </CardHeader>
              <CardBody>
                <AdminStateBlock
                  flash={flash}
                  onDismissFlash={() => setFlash(null)}
                  error={error}
                  onDismissError={() => setError("")}
                  loading={loading}
                  spinnerColor="info"
                  isEmpty={!visibleItems.length}
                  emptyTitle="İl bulunamadı"
                  emptyDescription="Filtreleri temizleyin veya yeni bir il ekleyin."
                  emptyIcon="fas fa-map-marked-alt"
                >
                  <Table className="align-items-center table-flush" responsive>
                    <thead className="thead-light">
                      <tr>
                        <th scope="col">İl</th>
                        <th scope="col">Temalar</th>
                        <th scope="col">İçerik</th>
                        <th scope="col">Durum</th>
                        <th scope="col">Güncellendi</th>
                        <th scope="col" className="text-right">İşlem</th>
                      </tr>
                    </thead>
                    <tbody>
                      {visibleItems.map((item) => (
                        <tr key={item.id}>
                          <td>
                            <div className="font-weight-bold">{item.name}</div>
                            <div className="small text-muted">{item.id}</div>
                            <div className="small text-muted">{item.slogan}</div>
                          </td>
                          <td>
                            {(item.themes || []).map((theme) => (
                              <Badge key={`${item.id}-${theme}`} color={themeColor(theme)} pill className="mr-1 mb-1">
                                {theme}
                              </Badge>
                            ))}
                          </td>
                          <td>
                            <div className="small">Highlights: {(item.highlights || []).length}</div>
                            <div className="small">Experiences: {(item.experiences || []).length}</div>
                            <div className="small">Seasons: {(item.seasons || []).length}</div>
                          </td>
                          <td>
                            <Badge color={item.deletedAt ? "warning" : "success"} pill>
                              {item.deletedAt ? "Arşivde" : "Aktif"}
                            </Badge>
                          </td>
                          <td>{item.updatedAt || "-"}</td>
                          <td className="text-right">
                            <Button
                              tag={Link}
                              to={`/admin${adminPaths.content.provinces.edit(item.id)}`}
                              color="primary"
                              size="sm"
                              className="mr-2"
                            >
                              Düzenle
                            </Button>
                            {item.deletedAt ? (
                              <Button color="success" outline size="sm" onClick={() => onRestore(item)}>
                                Geri al
                              </Button>
                            ) : (
                              <Button color="danger" size="sm" onClick={() => onArchive(item)}>
                                Arşivle
                              </Button>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </AdminStateBlock>
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
    </>
  );
}
